/**
 * Formulário de uma manutenção de veículo.
 *
 * Papel: preenche, valida e grava uma manutenção — o veículo, a data, a
 * quilometragem, o tipo de serviço, a descrição, a oficina, o valor e a próxima
 * revisão.
 *
 * Como usar: quem monta este componente passa `manutencao` para editar ou
 * `null` para criar, e deverá dar a ele uma `key` diferente por manutenção. Sem
 * a `key`, o React reaproveita a mesma instância ao trocar de registro e os
 * campos continuam com o que a pessoa via antes.
 *
 * O veículo é escolhido numa lista, e não digitado: um código digitado que
 * ninguém confere aponta para carro que não existe, e a manutenção fica órfã.
 *
 * Placa e modelo não são campos, e vêm do veículo escolhido. Tê-los aqui
 * permitiria que a manutenção dissesse uma placa e o cadastro dissesse outra.
 */
import {
  type ManutencaoVeiculo,
  type NovaManutencaoVeiculo,
  novaManutencaoVeiculoSchema,
} from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, type FieldPath, useForm } from 'react-hook-form';

import { aplicarErroDaApi, tradutorPorMapa } from '@/shared/lib/erro-de-formulario';
import { formatarPlaca } from '@/shared/lib/formato';
import { Button } from '@/shared/ui/button';
import { CampoMascarado } from '@/shared/ui/campo-mascarado';
import { DialogFooter } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import { useSalvarManutencao, useVeiculos } from '../hooks/usar-veiculos';
import { MANUTENCAO_EM_BRANCO, type ManutencaoNoFormulario } from '../types';

interface FormularioManutencaoProps {
  /** A manutenção a editar, ou `null` para criar uma nova. */
  manutencao: ManutencaoVeiculo | null;
  onSalvo: () => void;
  onCancelar: () => void;
}

/** Os campos que a API pode apontar, cada um no caminho do formulário. */
const CAMPOS: Record<string, FieldPath<ManutencaoNoFormulario> | undefined> = {
  veiculoId: 'veiculoId',
  data: 'data',
  km: 'km',
  tipo: 'tipo',
  descricao: 'descricao',
  oficina: 'oficina',
  valor: 'valor',
  proxData: 'proxData',
  proxKm: 'proxKm',
};

/**
 * Converte a manutenção vinda da API no formato que o formulário guarda.
 *
 * @param manutencao - A manutenção a editar, ou `null` para começar em branco.
 * @returns Os valores iniciais, com `null` trocado por string vazia.
 */
function valoresIniciais(manutencao: ManutencaoVeiculo | null): ManutencaoNoFormulario {
  if (manutencao === null) return { ...MANUTENCAO_EM_BRANCO };

  return {
    veiculoId: manutencao.veiculo.id,
    data: manutencao.data,
    km: manutencao.km === null ? '' : String(manutencao.km),
    tipo: manutencao.tipo,
    descricao: manutencao.descricao ?? '',
    oficina: manutencao.oficina ?? '',
    valor: manutencao.valor === null ? '' : String(manutencao.valor),
    proxData: manutencao.proxData ?? '',
    proxKm: manutencao.proxKm === null ? '' : String(manutencao.proxKm),
  };
}

/**
 * O formulário de criação e de edição de manutenção.
 *
 * @param manutencao - A manutenção a editar, ou `null` para criar.
 * @param onSalvo - Chamado depois de a API confirmar a gravação.
 * @param onCancelar - Chamado quando se desiste, sem gravar nada.
 * @returns O formulário completo, com os botões de ação.
 */
export function FormularioManutencao({
  manutencao,
  onSalvo,
  onCancelar,
}: FormularioManutencaoProps) {
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const frota = useVeiculos();
  const salvar = useSalvarManutencao();

  const form = useForm<ManutencaoNoFormulario, unknown, NovaManutencaoVeiculo>({
    resolver: zodResolver(novaManutencaoVeiculoSchema),
    defaultValues: valoresIniciais(manutencao),
  });

  const { errors } = form.formState;
  const enviando = salvar.isPending;

  /** Grava a manutenção e leva o erro da API para o campo que o causou. */
  const handleSalvar = form.handleSubmit(async (dados) => {
    setErroGeral(null);
    try {
      await salvar.mutateAsync({ id: manutencao?.id ?? null, dados });
      onSalvo();
    } catch (erro) {
      setErroGeral(aplicarErroDaApi(erro, form, tradutorPorMapa(CAMPOS)));
    }
  });

  return (
    <form onSubmit={handleSalvar}>
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="manutencao-veiculo">Veículo</Label>
          <Controller
            control={form.control}
            name="veiculoId"
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={enviando || frota.isPending}
              >
                <SelectTrigger id="manutencao-veiculo" className="w-full">
                  <SelectValue
                    placeholder={frota.isPending ? 'Carregando…' : 'Escolha o veículo'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {(frota.data ?? []).map((veiculo) => (
                    <SelectItem key={veiculo.id} value={veiculo.id}>
                      {veiculo.codigo} — {veiculo.marca} {formatarPlaca(veiculo.placa)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {frota.data?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Nenhum veículo cadastrado ainda — cadastre a frota antes.
            </p>
          )}
          {errors.veiculoId && (
            <p className="text-sm text-destructive" role="alert">
              {errors.veiculoId.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="manutencao-data">Data</Label>
          <Controller
            control={form.control}
            name="data"
            render={({ field }) => (
              <CampoMascarado
                id="manutencao-data"
                formato="data"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
                placeholder="DD/MM/AAAA"
              />
            )}
          />
          {errors.data && (
            <p className="text-sm text-destructive" role="alert">
              {errors.data.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="manutencao-km">Quilometragem</Label>
          <Input
            id="manutencao-km"
            inputMode="numeric"
            autoComplete="off"
            disabled={enviando}
            {...form.register('km')}
          />
          {errors.km && (
            <p className="text-sm text-destructive" role="alert">
              {errors.km.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="manutencao-tipo">Tipo de serviço</Label>
          <Input
            id="manutencao-tipo"
            autoComplete="off"
            placeholder="Revisão programada, Troca de pneus, Reparo…"
            disabled={enviando}
            {...form.register('tipo')}
          />
          {errors.tipo && (
            <p className="text-sm text-destructive" role="alert">
              {errors.tipo.message}
            </p>
          )}
        </div>

        <div className="col-span-full flex flex-col gap-1.5">
          <Label htmlFor="manutencao-descricao">Descrição</Label>
          <Textarea
            id="manutencao-descricao"
            rows={2}
            disabled={enviando}
            {...form.register('descricao')}
          />
          {errors.descricao && (
            <p className="text-sm text-destructive" role="alert">
              {errors.descricao.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="manutencao-oficina">Oficina</Label>
          <Input
            id="manutencao-oficina"
            autoComplete="off"
            disabled={enviando}
            {...form.register('oficina')}
          />
          {errors.oficina && (
            <p className="text-sm text-destructive" role="alert">
              {errors.oficina.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="manutencao-valor">Valor</Label>
          <Controller
            control={form.control}
            name="valor"
            render={({ field }) => (
              <CampoMascarado
                id="manutencao-valor"
                formato="dinheiro"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
              />
            )}
          />
          {errors.valor && (
            <p className="text-sm text-destructive" role="alert">
              {errors.valor.message}
            </p>
          )}
        </div>

        <fieldset className="col-span-full flex flex-col gap-3">
          <legend className="mb-2 font-mono text-[10.5px] tracking-[0.05em] text-texto-fraco">
            Próxima manutenção
          </legend>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="manutencao-prox-data">Data</Label>
              <Controller
                control={form.control}
                name="proxData"
                render={({ field }) => (
                  <CampoMascarado
                    id="manutencao-prox-data"
                    formato="data"
                    value={field.value}
                    onChange={field.onChange}
                    disabled={enviando}
                    placeholder="DD/MM/AAAA"
                  />
                )}
              />
              {errors.proxData && (
                <p className="text-sm text-destructive" role="alert">
                  {errors.proxData.message}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="manutencao-prox-km">Quilometragem</Label>
              <Input
                id="manutencao-prox-km"
                inputMode="numeric"
                autoComplete="off"
                disabled={enviando}
                {...form.register('proxKm')}
              />
              {errors.proxKm && (
                <p className="text-sm text-destructive" role="alert">
                  {errors.proxKm.message}
                </p>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Deixe em branco quando não houver revisão marcada. É o que transforma o histórico
            em agenda.
          </p>
        </fieldset>

        {erroGeral !== null && (
          <p className="col-span-full text-sm text-destructive" role="alert">
            {erroGeral}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="ghost" disabled={enviando} onClick={onCancelar}>
          Cancelar
        </Button>
        <Button type="submit" disabled={enviando}>
          {enviando ? 'Salvando…' : 'Salvar manutenção'}
        </Button>
      </DialogFooter>
    </form>
  );
}
